import { Brain, RefreshCw } from "lucide-react";
import { useAIAnalysisProgress } from "@/hooks/useAIAnalysisProgress";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface AIAnalysisProgressProps {
	entityId: string | undefined;
	entityType: "candidate" | "job" | "match";
	className?: string;
	onRefresh?: () => void;
}

export const AIAnalysisProgress = ({ entityId, entityType, className = "", onRefresh }: AIAnalysisProgressProps) => {
	const { isProcessing, progress, timeRemaining, isCompleted, resetCompleted } = useAIAnalysisProgress(entityId, entityType);
	const { t } = useTranslation();

	// Don't show anything if never processing and not completed
	if (!isProcessing && !isCompleted) {
		return null;
	}

	const progressPercentage = Math.round(progress * 100);
	const remainingSeconds = Math.ceil(timeRemaining);

	// Handle completion state
	if (isCompleted) {
		return (
			<div className={`w-full bg-green-50 border border-green-200 rounded-sm p-3 mb-4 ${className}`}>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Brain className="h-4 w-4 text-green-600" />
						<p className="text-xs font-medium text-green-900">
							{t("common.aiAnalysisComplete", "AI Analysis complete")}
						</p>
					</div>					<Button
						size="sm"
						variant="outline"
						onClick={() => {
							resetCompleted();
							onRefresh?.();
						}}
						className="flex items-center gap-1 text-xs h-7 px-2 border-green-300 text-green-700 hover:bg-green-100"
					>
						<RefreshCw className="h-3 w-3" />
						{t("common.seeResults", "See Results")}
					</Button>
				</div>
			</div>
		);
	}

	// Processing state
	return (
		<div className={`w-full p-3 mb-4 ${className}`}>
			<div className="flex items-center gap-2 mb-2">
				<Brain className="h-4 w-4 animate-pulse" />
				<div className="flex-1">
					<p className="text-xs font-medium text-blue-900">
						{t("common.aiAnalysisInProgress", "AI Analysis in progress")}
					</p>
				</div>
			</div>
			
			{/* Progress Bar */}
			<div className="w-full rounded-sm h-1 overflow-hidden">
				<div
					className="h-1 bg-blue-500 rounded-sm transition-all duration-1000 ease-out"
					style={{ width: `${progressPercentage}%` }}
				/>
			</div>
			
			{remainingSeconds > 10 && (
				<div className="flex justify-between text-xs text-blue-500 mt-1 opacity-75">
					<span>{progressPercentage}%</span>
					<span>{t("common.processing", "Processing...")}</span>
				</div>
			)}
		</div>
	);
};
